import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { CommunityProviderProgramSearchListService } from 'src/app/core/service/grid-data/grid-data-community-search.service';
import { MemberService } from 'src/app/core/service/member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Member } from 'src/app/member/model/member';
import { CommunityProviderSearchResult } from '../model/community-provider-search-result';
import { TranslateService } from '@ngx-translate/core';
import { CommunityProviderProgramService } from 'src/app/core/service/community-provider-program.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';

@Component({
  standalone: false,
  selector: 'app-referral-add',
  templateUrl: './referral-add.component.html',
  styleUrls: ['./referral-add.component.scss']
})
export class ReferralAddComponent implements OnInit
{
  @Input() public memberId: number;
  @Input() public serviceCategories = [];
  @Input() public excludedServices = [];
  @Input() public referralReassign = false;

  @Output() public closeNewReferral = new EventEmitter<boolean>();
  @Output() public sendReferrals = new EventEmitter<{ selectedItems: number[]; sendEmailNotification: boolean }>();

  public newReferralDialogOpened = false;
  public windowState = 'maximized';
  public member: Member;
  public selectedPrograms: any = [];
  public selectedItems: number[] = [];
  public sendReferralDialogOpened = false;
  public missingReferralContactEmail = false;
  public patientLabel = {};
  public cancelConfirmationDialogOpened = false;
  public sendEmailNotification = false;
  public sendEmailNotificationDisabled = false;
  public userOrganizationName = '';
  public defaultResourceStatuses = [];

  private informationalOnlyProgramsMessage = '';
  private inactiveProgramsMessage = '';
  private informationalOnlyInactiveProgramsMessage = '';

  constructor(
    private settingService: SettingService,
    private memberService: MemberService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private communityProviderProgramSearchList: CommunityProviderProgramSearchListService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private communityProviderProgramService: CommunityProviderProgramService) { }

  @Input() public set dialogOpened(value: boolean)
  {
    if (value)
    {
      const currentUser = this.authenticationService.currentUser;
      this.userService.getUserById(currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.userOrganizationName = user.organization ? user.organization.name : '';

          // Get the patient address
          this.memberService.getMember(this.memberId).subscribe({
            next: (member) =>
            {
              this.member = member;
              this.sendEmailNotification = false;
              this.sendEmailNotificationDisabled = (member.contact === null ||
                !(member.notificationType === 'EMAIL' || member.notificationType === 'SMS'));

              this.selectedItems = [];
              this.newReferralDialogOpened = true;
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      });
    }
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.defaultResourceStatuses = sessionSetting.defaultResourceStatuses ? sessionSetting.defaultResourceStatuses.split(',') : [];
      }
    });
    this.translate.get(['COMMUNITY-PROGRAMS-INFORMATIONAL-ONLY-SELECTED', 'COMMUNITY-PROGRAMS-INACTIVE-SELECTED', 'COMMUNITY-PROGRAMS-INFORMATIONAL-ONLY-INACTIVE-SELECTED']).subscribe({
      next: (res) =>
      {
        this.informationalOnlyProgramsMessage = res['COMMUNITY-PROGRAMS-INFORMATIONAL-ONLY-SELECTED'];
        this.inactiveProgramsMessage = res['COMMUNITY-PROGRAMS-INACTIVE-SELECTED'];
        this.informationalOnlyInactiveProgramsMessage = res['COMMUNITY-PROGRAMS-INFORMATIONAL-ONLY-INACTIVE-SELECTED'];
      }
    });
  }

  /**
   * Close the modal
   */
  public closeModal(isCloseOrCancelReferral: boolean): void
  {
    this.newReferralDialogOpened = false;
    this.closeNewReferral.emit(isCloseOrCancelReferral);
  }

  public cancelConfirmationModal(): void
  {
    this.newReferralDialogOpened = false;
    this.cancelConfirmationDialogOpened = true;
  }

  public cancelConfirmationAction(confirmed: boolean): void
  {
    this.cancelConfirmationDialogOpened = false;
    if (confirmed)
    {
      this.closeModal(true);
    }
    else
    {
      this.newReferralDialogOpened = true;
    }
  }

  /**
   * Send New Referrals
   */
  public onSendNewReferral(): void
  {
    // Closing the results form
    this.newReferralDialogOpened = false;
    this.closeModal(false);

    const state: State = {
      skip: 0,
      take: this.selectedItems.length,
      filter: {
        logic: 'and',
        filters: [{ field: 'resourceStatusCode', operator: 'neq', value: 'inactive' }]
      },
      sort: [{ field: 'name', dir: 'asc' }]
    };
    this.selectedItems.forEach((id: number) =>
    {
      state.filter.filters.push({ field: 'id', operator: 'eq', value: id, logic: 'or' });
    });

    this.communityProviderProgramSearchList.queryByState(state, (value: CommunityProviderSearchResult) =>
    {
      this.missingReferralContactEmail = false;
      this.selectedPrograms = [];
      let informationalOnlyPrograms = '';
      value.data.forEach((program: any) =>
      {
        program.referralNote = null;
        if (program.informationalOnly === true)
        {
          informationalOnlyPrograms = informationalOnlyPrograms.concat(program.name).concat(', ');
        }
        else
        {
          this.selectedPrograms.push(program);
          if (program.referralEmail === null || program.referralEmail === '')
          {
            this.missingReferralContactEmail = true;
          }
        }
      });

      let inactivePrograms = '';
      const inactiveState: State = {
        skip: 0,
        take: this.selectedItems.length,
        filter: {
          logic: 'and',
          filters: [{ field: 'resourceStatusCode', operator: 'eq', value: 'inactive' }]
        },
        sort: [{ field: 'name', dir: 'asc' }]
      };
      this.selectedItems.forEach((id: number) =>
      {
        inactiveState.filter.filters.push({ field: 'id', operator: 'eq', value: id, logic: 'or' });
      });

      this.communityProviderProgramSearchList.queryByState(inactiveState, (value: CommunityProviderSearchResult) =>
      {
        value.data.forEach((program: any) =>
        {
          inactivePrograms = inactivePrograms.concat(program.name).concat(', ');
        });

        if (informationalOnlyPrograms !== '' && inactivePrograms === '')
        {
          informationalOnlyPrograms = informationalOnlyPrograms.substring(0, informationalOnlyPrograms.length - 2);
          this.informationalOnlyProgramsMessage = this.informationalOnlyProgramsMessage.replace('{{informationalOnlyPrograms}}', informationalOnlyPrograms)
          this.messageHandlerService.displayDirectMessage('warning', this.informationalOnlyProgramsMessage);
        }
        else
          if (informationalOnlyPrograms === '' && inactivePrograms !== '')
          {
            inactivePrograms = inactivePrograms.substring(0, inactivePrograms.length - 2);
            this.inactiveProgramsMessage = this.inactiveProgramsMessage.replace('{{inactivePrograms}}', inactivePrograms);
            this.messageHandlerService.displayDirectMessage('warning', this.inactiveProgramsMessage);
          }
          else
            if (informationalOnlyPrograms !== '' && inactivePrograms !== '')
            {
              // check if any of these programs are both inactive and informational only
              informationalOnlyPrograms = informationalOnlyPrograms.substring(0, informationalOnlyPrograms.length - 2);
              inactivePrograms = inactivePrograms.substring(0, inactivePrograms.length - 2);
              this.informationalOnlyInactiveProgramsMessage =
                this.informationalOnlyInactiveProgramsMessage.replace('{{informationalOnlyPrograms}}', informationalOnlyPrograms).replace('{{inactivePrograms}}', inactivePrograms);
              this.messageHandlerService.displayDirectMessage('warning', this.informationalOnlyInactiveProgramsMessage);
            }

      });
      this.sendReferralDialogOpened = (this.selectedPrograms.length !== 0);
      // We must re-enable CLOSE button
      if (this.selectedPrograms.length == 0) 
      {
        this.messengerService.sendMessage(new MessageSubject('enableReferralCloseButton'));
      }
    });
  }

  /**
   * New Services have been selected.
   *
   * @param e Selected ids.
   */
  public onSelectedKeysChange(e: any): void
  {
    this.selectedItems = e;
  }

  /**
   * Button action handler
   *
   * @param confirm Confirm the action: yes/no
   */
  public sendReferralAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Prepare selected services and comments
      const selectedServices = [];
      this.selectedItems.forEach((selected) =>
      {
        const referral = this.selectedPrograms.find(x => x.id == selected);
        if (referral !== undefined)
        {
          selectedServices.push({ id: referral.id, note: referral.referralNote, consentToRefer: true });
        }
      });
      this.sendReferrals.emit({ selectedItems: selectedServices, sendEmailNotification: this.sendEmailNotification });
    }
    else
    {
      // closeModel(true) will perform and will make sure afterReferralProcessing.emit() is performed referral.component
      // In this case, user did not sent the new referral but we still need to complete the task.
      this.closeModal(true);
    }
    this.sendReferralDialogOpened = false;
    this.selectedPrograms = [];
  }
}
